import { useState, useCallback } from 'react';

import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
  Tooltip,
} from '@mui/material';
import { t } from 'i18next';

import { GeneratedImage } from '../utils/types';
import { GradientButton } from './minicomponents/GradientButton';
import {
  IconDelete,
  IconDownload,
  IconGenerateImage,
  IconError,
  IconReload,
} from './minicomponents/icons';
import ImageGenerateConfirmPopup from './popups/ImageGenerateConfirmPopup';

interface ImageGalleryProps {
  images: GeneratedImage[];
  onGenerate: () => Promise<void>;
  onDelete: (imageId: string) => Promise<void>;
  onRetry?: (imageId: string) => Promise<void>;
  isGenerating?: boolean;
  disabled?: boolean;
  creditsLeft: number;
}

export const ImageGallery: React.FC<ImageGalleryProps> = ({
  images,
  onGenerate,
  onDelete,
  onRetry,
  isGenerating = false,
  disabled = false,
  creditsLeft,
}) => {
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    imageId: string;
    imageUrl: string;
  }>({ open: false, imageId: '', imageUrl: '' });
  const [deletingImages, setDeletingImages] = useState<Set<string>>(new Set());
  const [previewDialog, setPreviewDialog] = useState<{
    open: boolean;
    imageUrl: string;
    imagePrompt: string;
    imageWidth?: number;
    imageHeight?: number;
  }>({ open: false, imageUrl: '', imagePrompt: '' });
  const [confirmDialog, setConfirmDialog] = useState(false);

  const handleGenerateClick = useCallback(async () => {
    if (disabled || isGenerating || confirmDialog) return;
    setConfirmDialog(true);
  }, [disabled, isGenerating, confirmDialog]);

  const handleConfirmGenerate = async () => {
    setConfirmDialog(false);
    try {
      await onGenerate();
    } catch (error) {
      console.error('Failed to generate image:', error);
      // Error handling is done in parent component
    }
  };

  const handleDeleteClick = (imageId: string, imageUrl: string) => {
    setDeleteDialog({ open: true, imageId, imageUrl });
  };

  const handleDeleteConfirm = async () => {
    const { imageId } = deleteDialog;
    if (!imageId) return;

    setDeletingImages((prev) => new Set(prev).add(imageId));

    try {
      await onDelete(imageId);
    } catch (error) {
      console.error('Failed to delete image:', error);
    } finally {
      setDeletingImages((prev) => {
        const newSet = new Set(prev);
        newSet.delete(imageId);
        return newSet;
      });
      setDeleteDialog({ open: false, imageId: '', imageUrl: '' });
    }
  };

  const handleDownload = async (imageUrl: string) => {
    const filename = `aiter-generated-image-${Date.now()}.png`;

    try {
      console.log('Starting download for:', imageUrl);

      // Fetch the image data
      const response = await fetch(imageUrl, {
        method: 'GET',
        mode: 'cors',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const blob = await response.blob();
      console.log('Blob created:', blob.size, 'bytes');

      // Create blob URL
      const blobUrl = URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = filename;
      link.style.display = 'none';

      // Ensure the link is added to DOM, clicked, then removed
      document.body.appendChild(link);

      // Force click with a small delay to ensure it's in DOM
      setTimeout(() => {
        link.click();
        console.log('Download link clicked');

        // Clean up after download
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
          URL.revokeObjectURL(blobUrl);
          console.log('Cleanup completed');
        }, 1000);
      }, 100);
    } catch (error) {
      console.error('Download failed:', error);

      // Show user-friendly error and provide manual option
      alert(
        `Download failed: ${error instanceof Error ? error.message : 'Unknown error'}\n\nThe image will open in a new tab. Please right-click and select "Save image as..." to download it.`
      );
      window.open(imageUrl, '_blank');
    }
  };

  const handleImageClick = (imageUrl: string, imagePrompt: string) => {
    // Load image to get dimensions
    const img = new Image();
    img.onload = () => {
      setPreviewDialog({
        open: true,
        imageUrl,
        imagePrompt,
        imageWidth: img.naturalWidth,
        imageHeight: img.naturalHeight,
      });
    };
    img.onerror = () => {
      // If image fails to load, still show dialog without dimensions
      setPreviewDialog({ open: true, imageUrl, imagePrompt });
    };
    img.src = imageUrl;
  };

  const canGenerateMore = !disabled;

  // Helper function to render image based on status
  const renderImageContent = (image: GeneratedImage) => {
    const imageStatus = image.status || 'completed'; // Default to completed for backward compatibility

    if (imageStatus === 'loading') {
      return (
        <Box
          sx={{
            width: '100%',
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.05)',
            borderRadius: 2,
          }}
        >
          <CircularProgress size={40} sx={{ mb: 2 }} />
          <Typography variant="body2" color="text.secondary" align="center">
            {t('app.structuredItems.actions.generateImage.generatingImage')}
          </Typography>
        </Box>
      );
    }

    if (imageStatus === 'failed') {
      return (
        <Box
          sx={{
            width: '100%',
            height: 200,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'rgba(244, 67, 54, 0.05)',
            borderRadius: 2,
            border: '1px dashed rgba(244, 67, 54, 0.3)',
            cursor: onRetry ? 'pointer' : 'default',
          }}
          onClick={onRetry ? () => onRetry(image.id) : undefined}
        >
          <IconError sx={{ fontSize: 40, color: 'error.main', mb: 1 }} />
          <Typography variant="body2" color="error.main" align="center" sx={{ mb: 1 }}>
            {t('app.structuredItems.actions.generateImage.generationFailed')}
          </Typography>
          {onRetry && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <IconReload sx={{ fontSize: 16, color: 'primary.main' }} />
              <Typography variant="caption" color="primary.main">
                {t('app.structuredItems.actions.generateImage.retryGeneration')}
              </Typography>
            </Box>
          )}
        </Box>
      );
    }

    // Completed status - render actual image
    return image.url ? (
      <Box
        component="img"
        src={image.url}
        alt={image.prompt}
        sx={{
          width: '100%',
          height: 200,
          objectFit: 'cover',
          display: 'block',
        }}
      />
    ) : (
      // Fallback for corrupted data
      <Box
        sx={{
          width: '100%',
          height: 200,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'rgba(0, 0, 0, 0.05)',
          borderRadius: 2,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {t('app.structuredItems.actions.generateImage.imageNotAvailable')}
        </Typography>
      </Box>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      {/* Images Grid */}
      {images.length > 0 && (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          {images.map((image) => {
            const imageStatus = image.status || 'completed';
            const isCompleted = imageStatus === 'completed' && image.url;
            const isLoading = imageStatus === 'loading';
            const showDownloadAction = isCompleted && !deletingImages.has(image.id);
            const showDeleteAction = !deletingImages.has(image.id); // Allow delete for all states

            return (
              <Grid size={{ mobile: 12, tablet: 6, laptop: 4 }} key={image.id}>
                <Box
                  sx={{
                    position: 'relative',
                    borderRadius: 2,
                    overflow: 'hidden',
                    cursor: isCompleted ? 'pointer' : 'default',
                  }}
                  onClick={
                    isCompleted
                      ? () => handleImageClick(image.url!, image.prompt)
                      : undefined
                  }
                >
                  {renderImageContent(image)}
                  {(showDownloadAction || showDeleteAction) && (
                    <Box
                      className="image-actions"
                      sx={{
                        position: 'absolute',
                        top: 8,
                        right: 8,
                        display: 'flex',
                        gap: 1,
                        opacity: 1,
                      }}
                    >
                      {/* Download action - only for completed images */}
                      {showDownloadAction && (
                        <Tooltip
                          title={t(
                            'app.structuredItems.actions.generateImage.downloadButton'
                          )}
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (image.url) {
                                handleDownload(image.url);
                              }
                            }}
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              width: 32,
                              height: 32,
                            }}
                          >
                            <IconDownload sx={{ fontSize: '16px' }} />
                          </IconButton>
                        </Tooltip>
                      )}

                      {/* Delete action - for all states */}
                      {showDeleteAction && (
                        <Tooltip
                          title={
                            isLoading
                              ? t(
                                  'app.structuredItems.actions.generateImage.cancelGeneration'
                                )
                              : t(
                                  'app.structuredItems.actions.generateImage.deleteButton'
                                )
                          }
                        >
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(image.id, image.url || '');
                            }}
                            disabled={deletingImages.has(image.id)}
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.9)',
                              '&:hover': {
                                backgroundColor: 'rgba(255, 255, 255, 1)',
                              },
                              width: 32,
                              height: 32,
                            }}
                          >
                            {deletingImages.has(image.id) ? (
                              <CircularProgress size={16} />
                            ) : (
                              <IconDelete sx={{ fontSize: '16px' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  )}
                </Box>
              </Grid>
            );
          })}
        </Grid>
      )}

      {/* Generate Button */}
      {/* <Box sx={{ mt: 2, display: 'none' }}> */}
      <Box sx={{ mt: 2 }}>
        <GradientButton
          onSubmitButtonHandler={handleGenerateClick}
          buttonText={
            isGenerating
              ? t('app.labels.generationInProgress')
              : t('app.structuredItems.actions.generateImage.buttonText')
          }
          version="text"
          iconLeft={
            isGenerating ? <CircularProgress size={16} /> : <IconGenerateImage />
          }
          disabled={!canGenerateMore || isGenerating}
        />
      </Box>

      {/* Image Preview Dialog */}
      <Dialog
        open={previewDialog.open}
        onClose={() => setPreviewDialog({ open: false, imageUrl: '', imagePrompt: '' })}
        maxWidth="tablet"
        PaperProps={{
          sx: {
            maxWidth: '600px',
            maxHeight: '90vh',
            width: 'auto',
          },
        }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            p: 1,
            minHeight: 'auto',
          }}
        >
          <IconButton
            onClick={() =>
              setPreviewDialog({ open: false, imageUrl: '', imagePrompt: '' })
            }
            sx={{
              color: 'text.secondary',
              backgroundColor: 'rgba(0, 0, 0, 0.05)',
              width: 40,
              height: 40,
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.1)',
              },
            }}
          >
            <Box
              component="span"
              sx={{
                fontSize: '24px',
                fontWeight: 'bold',
                lineHeight: 1,
              }}
            >
              ×
            </Box>
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ p: 0 }}>
          {previewDialog.imageUrl && (
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <img
                src={previewDialog.imageUrl}
                alt={previewDialog.imagePrompt}
                style={{
                  maxWidth: '100%',
                  maxHeight: '80vh',
                  objectFit: 'contain',
                  display: 'block',
                }}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog({ open: false, imageId: '', imageUrl: '' })}
        maxWidth="tablet"
        PaperProps={{
          sx: {
            maxWidth: '500px',
            width: 'auto',
          },
        }}
      >
        <DialogTitle>Delete Image</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete this image?
          </DialogContentText>
          {deleteDialog.imageUrl && (
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <img
                src={deleteDialog.imageUrl}
                alt="Preview"
                style={{
                  maxWidth: '100%',
                  maxHeight: '400px',
                  objectFit: 'contain',
                }}
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteDialog({ open: false, imageId: '', imageUrl: '' })}
            disabled={deletingImages.has(deleteDialog.imageId)}
          >
            {t('app.buttons.cancel')}
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingImages.has(deleteDialog.imageId)}
            startIcon={
              deletingImages.has(deleteDialog.imageId) ? (
                <CircularProgress size={16} />
              ) : undefined
            }
          >
            {deletingImages.has(deleteDialog.imageId)
              ? t('app.labels.deleting')
              : t('app.buttons.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Image Generate Confirmation Dialog */}
      <ImageGenerateConfirmPopup
        open={confirmDialog}
        onClose={() => setConfirmDialog(false)}
        onConfirm={handleConfirmGenerate}
        creditsLeft={creditsLeft}
        isGenerating={isGenerating}
      />
    </Box>
  );
};
